import * as React from 'react';

import { Alert as AntdAlert, App, Button, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';

import Alert from 'components/Alert';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { WebhookStatus, WebhookStatusAlias, WebhookTypeAlias } from 'v2/interfaces/webhook2';
import type { Webhook } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import { Icon } from '@eduzz/ui-antd-theme';

import { useWebhookContext } from '../context';

type Props = {
  webhook: Webhook;
};

const Actions = ({ webhook }: Props) => {
  const { message } = App.useApp();
  const { setIsFormOpen, listWebhooks, setSelectedWebhook, setLoading } = useWebhookContext();

  const onFinish = () => {
    setSelectedWebhook(null);
    listWebhooks();
  };

  const onClose = () => {
    setSelectedWebhook(null);
  };

  const handleEdit = () => {
    setSelectedWebhook(webhook);
    setIsFormOpen(true);
  };

  const handleActive = async () => {
    const diff = differenceInSeconds(new Date(), new Date(webhook.updated_at));

    if (diff < 30) {
      Alert.show({
        title: 'Aguarde',
        message: 'Aguarde pelo menos 30s antes de poder ativar/reativar o mesmo webhook.',
        buttonOk: 'Aguardar'
      });
      onFinish();
      return;
    }

    const confirm = await Alert.confirm({
      message: (
        <div className='flex flex-col gap-2'>
          <Typography.Paragraph className='mb-0'>Deseja realmente ativar esse webhook?</Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>URL:</Typography.Text> {webhook.url}
          </Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>Tipo:</Typography.Text>{' '}
            {WebhookTypeAlias[webhook.type as keyof typeof WebhookTypeAlias]}
          </Typography.Paragraph>
        </div>
      ),
      title: 'Atenção, você está ativando um webhook.',
      confirmation: true
    });

    if (confirm) {
      try {
        setLoading(true);
        const response = await webhookService.reactive(webhook._id);

        if (response.success === true) {
          onFinish();
          message.success('Webhook ativo com sucesso');
        } else {
          onFinish();
          message.error('URL do webhook Inválida');
        }
      } catch (e) {
        message.error(typeof e === 'string' ? e : 'Não foi possível ativar o webhook.');
      }
    }

    onClose();
  };

  const handleInactive = async () => {
    const diff = differenceInSeconds(new Date(), new Date(webhook.updated_at));

    if (diff < 30) {
      Alert.show({
        title: 'Aguarde',
        message: 'Aguarde pelo menos 30s antes de poder ativar/reativar o mesmo webhook.',
        buttonOk: 'Aguardar'
      });
      onFinish();
      return;
    }

    const confirm = await Alert.confirm({
      message: (
        <div className='flex flex-col gap-3'>
          <Typography.Paragraph className='mb-0'>Deseja realmente desativar esse webhook?</Typography.Paragraph>

          <AntdAlert
            type='error'
            showIcon
            message='Ao inativar sua url, todos os eventos pendentes e com erro serão arquivados.'
          />

          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>URL:</Typography.Text> {webhook.url}
          </Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>Tipo:</Typography.Text>{' '}
            {WebhookTypeAlias[webhook.type as keyof typeof WebhookTypeAlias]}
          </Typography.Paragraph>
        </div>
      ),
      title: 'Atenção, você está desativando um webhook.',
      confirmation: true
    });

    if (confirm) {
      try {
        setLoading(true);
        await webhookService.inactive(webhook._id);
        onFinish();
        message.success('Webhook desativado com sucesso');
      } catch (e) {
        message.error(typeof e === 'string' ? e : 'Não foi possível desativar o webhook.');
      }
    }

    onClose();
  };

  const handleRemove = async () => {
    const confirm = await Alert.confirm({
      message: (
        <div className='flex flex-col gap-2'>
          <Typography.Paragraph className='mb-0'>Deseja realmente remover esse webhook?</Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>URL:</Typography.Text> {webhook.url}
          </Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>Tipo:</Typography.Text>{' '}
            {WebhookTypeAlias[webhook.type as keyof typeof WebhookTypeAlias]}
          </Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>Status:</Typography.Text> {WebhookStatusAlias[webhook.state]}
          </Typography.Paragraph>
          <Typography.Paragraph className='mb-0'>
            <Typography.Text strong>Atenção: </Typography.Text>
            ao remover o webhook todos os históricos serão removidos.
          </Typography.Paragraph>
        </div>
      ),
      title: 'Atenção, você está removendo um webhook.',
      confirmation: true
    });

    if (confirm) {
      try {
        setLoading(true);
        await webhookService.removeWebhook(webhook._id);
        message.success('Webhook removido com sucesso');
        onFinish();
      } catch (e) {
        message.error(typeof e === 'string' ? e : 'Não foi possível remover o webhook.');
      }
    }

    onClose();
  };

  const menuItems: MenuProps['items'] = [
    { key: 'edit', label: 'Editar', icon: <Icon.Pencil size={16} /> },
    ...(webhook.state === WebhookStatus.inactive
      ? [{ key: 'activate', label: 'Ativar', icon: <Icon.Play size={16} /> }]
      : []),
    ...(webhook.state === WebhookStatus.failing || webhook.state === WebhookStatus.active
      ? [{ key: 'deactivate', label: 'Desativar', icon: <Icon.CircleStop size={16} /> }]
      : []),
    {
      key: 'remove',
      label: 'Remover',
      icon: <Icon.Trash2 size={16} />,
      danger: true
    }
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'edit') handleEdit();
    if (key === 'activate') handleActive();
    if (key === 'deactivate') handleInactive();
    if (key === 'remove') handleRemove();
  };

  return (
    <PermissionHide action={'myeduzz:webhook:editar'}>
      <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
        <Button
          type='text'
          shape='circle'
          aria-label='Ações do webhook'
          icon={<Icon.Ellipsis size={16} />}
          onClick={e => e.stopPropagation()}
        />
      </Dropdown>
    </PermissionHide>
  );
};

export { Actions };
